import { useQuery } from '@tanstack/react-query';
import { Hash, Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  getContext,
  querySymbols,
  type ContextResult,
  type QueryHit,
  type QueryResult,
} from '../api/symbols.js';
import { useMcp } from '../api/use-mcp.js';
import { PageHeader } from '../components/page-header.js';
import { SubgraphViewer } from '../components/subgraph-viewer.js';
import { cn } from '../lib/cn.js';

function PanelList({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly { id: string; name?: string }[] | undefined;
}): JSX.Element {
  return (
    <div className="min-w-[180px] flex-1 rounded-xl border border-border-subtle bg-surface/60 p-3 backdrop-blur">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">{title}</h3>
      {!items || items.length === 0 ? (
        <p className="mt-2 text-xs text-text-muted">None</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-center gap-1.5 rounded-md px-1.5 py-1 font-mono text-xs text-text-secondary hover:bg-hover hover:text-text-primary"
            >
              <Hash className="h-3 w-3 text-text-muted" />
              <span className="truncate">{it.name ?? it.id}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SymbolExplorer(): JSX.Element {
  const { name } = useParams<{ name: string }>();
  const { handle, ready } = useMcp();
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [picked, setPicked] = useState<QueryHit | null>(null);

  const search = useQuery<QueryResult>({
    queryKey: ['mcp-query', name, submitted],
    queryFn: () => querySymbols(handle!, { repo: name ?? '', text: submitted, topK: 20 }),
    enabled: ready && submitted.length > 0 && Boolean(name),
  });

  const ctx = useQuery<ContextResult>({
    queryKey: ['mcp-context', name, picked?.nodeId],
    queryFn: () => getContext(handle!, { repo: name ?? '', symbol: picked!.nodeId }),
    enabled: ready && picked !== null && Boolean(name),
  });

  if (!name) return <p className="p-8 text-text-muted">Missing repo.</p>;

  return (
    <section className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        eyebrow={name}
        title="Symbol explorer"
        subtitle="Hybrid BM25 + vector search across the indexed graph."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(text);
          setPicked(null);
        }}
        className="flex items-center gap-2 rounded-2xl border border-border-subtle bg-surface/60 p-2 backdrop-blur"
      >
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border-subtle bg-deep px-3 py-2 transition-all focus-within:border-accent/40 focus-within:ring-2 focus-within:ring-accent/20">
          <Search className="h-4 w-4 text-text-muted" />
          <input
            type="search"
            placeholder="Search symbols (e.g. createUser)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Symbol search"
            className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
        <button
          type="submit"
          disabled={!ready}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-accent to-accent-dim px-4 py-2 text-sm font-semibold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          Search
        </button>
      </form>

      {search.isFetching && (
        <p className="flex items-center gap-2 font-mono text-xs text-text-secondary">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
          Searching…
        </p>
      )}
      {search.error && (
        <p className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
          {String(search.error)}
        </p>
      )}
      {(search.data?.errors.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-1">
          {search.data!.errors.map((e) => (
            <li
              key={e.slug}
              className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 font-mono text-xs text-amber-300"
            >
              <span className="font-semibold">{e.slug}:</span> {e.error}
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        <ul className="scrollbar-thin max-h-[480px] overflow-auto rounded-2xl border border-border-subtle bg-surface/60 p-1 backdrop-blur">
          {(search.data?.hits ?? []).map((hit) => (
            <li key={hit.nodeId}>
              <button
                type="button"
                onClick={() => setPicked(hit)}
                className={cn(
                  'group flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left transition-colors',
                  picked?.nodeId === hit.nodeId
                    ? 'bg-accent/15 text-text-primary'
                    : 'hover:bg-hover',
                )}
              >
                <span className="truncate font-mono text-xs text-text-primary">{hit.nodeId}</span>
                {hit.filePath && (
                  <span className="truncate text-[10px] text-text-muted">{hit.filePath}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <div className="grid gap-4">
          {picked ? (
            <>
              <div className="flex flex-wrap gap-3">
                <PanelList title="Callers" items={ctx.data?.callers} />
                <PanelList title="Callees" items={ctx.data?.callees} />
                <PanelList title="Processes" items={ctx.data?.processes} />
              </div>
              <SubgraphViewer context={ctx.data ?? null} />
            </>
          ) : (
            <div className="grid place-items-center rounded-2xl border border-dashed border-border-subtle bg-surface/40 px-6 py-16 text-center text-sm text-text-muted">
              Pick a search hit to see its context.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
